import { call, put, select, delay } from 'redux-saga/effects';
import { callWithAuth } from '../../sagaApiHelper';
import { createTemplateApi } from '../../../api/apiLists/createTemplateApi';
import { buildTemplateRequest as buildTemplateRequestUtil } from '../../../utils/buildTemplateRequest';
import { showAlert } from '../../alertSlice';
import { setLoading, setSuccess, setError, resetWhatsappState, addTemplate, clearMediaVerification } from '../../whatsappSlice';
import { performMediaVerification } from './mediaVerificationUtils';
import { getOrFetchWabaId } from './utils';
import { HARDCODED_S3_VERIFICATION } from '../../../utils/hardcodedMediaConfig';
import { getAuthOtpButtonText } from '../../../pages/whatsapp/constants/templateConstants';

export function* handleCreateTemplate(action) {
  yield put(setLoading(true));

  try {
    const payload = action.payload || {};
    const companyInfo = yield select(state => state.companyInfo?.info || {});
    const companyId = yield select(state => state.auth?.user?.companyId || companyInfo?.companyId || companyInfo?.id);

    if (!companyId) {
      yield put(setError('Company ID not found'));
      yield put(showAlert({ message: 'Company ID not found. Please contact support.', type: 'error' }));
      return;
    }

    const wabaId = yield* getOrFetchWabaId();
    if (!wabaId) {
      yield put(showAlert({ message: 'WhatsApp Business Account not found for your company. Please Connect with Meta first.', type: 'error' }));
      return;
    }

    const categoryValue = Array.isArray(payload.category)
      ? payload.category[0]?.toUpperCase()
      : typeof payload.category === 'object'
        ? payload.category?.value?.toUpperCase()
        : ((payload.category || '').toUpperCase());

    const categoryNormalized = categoryValue || 'UTILITY';
    const isAuthTemplate = payload.isAuthTemplate || false;

    let headerHandle = null;
    const mediaFile = payload.mediaFile;
    const mediaType = payload.mediaType;

    // Skip media verification for authentication templates
    if (!isAuthTemplate && mediaType !== 'none') {
      let mediaVerifyPayload;

      if (['image', 'video', 'document', 'audio'].includes(mediaType)) {
        const s3Config = HARDCODED_S3_VERIFICATION.media[mediaType];
        mediaVerifyPayload = {
          wabaId,
          appId: process.env.REACT_APP_FACEBOOK_SDK_APP_ID,
          mimeType: s3Config.mimeType,
          source: 's3',
          bucket: HARDCODED_S3_VERIFICATION.bucket,
          key: s3Config.key
        };
      } else {
        // For other media types, use fileBase64 (if any)
        let fileBase64 = mediaFile.preview;
        const fileName = mediaFile.file?.name;
        let fileMimeType = mediaFile.file?.type;
        let fileLength = mediaFile.file?.size || 0;

        if (fileBase64?.startsWith('/')) {
          try {
            const response = yield call(fetch, fileBase64);
            const blob = yield call(() => response.blob());
            fileBase64 = yield new Promise((resolve, reject) => {
              const reader = new FileReader();
              reader.onloadend = () => resolve(reader.result);
              reader.onerror = reject;
              reader.readAsDataURL(blob);
            });
            fileLength = blob.size;
            fileMimeType = blob.type;
          } catch {
            yield put(setError('Failed to prepare media'));
            yield put(showAlert({ message: 'Failed to prepare media for upload', type: 'error' }));
            return;
          }
        }

        mediaVerifyPayload = {
          wabaId,
          appId: process.env.REACT_APP_FACEBOOK_SDK_APP_ID,
          fileName,
          fileLength,
          mimeType: fileMimeType,
          fileBase64
        };
      }

      const verificationResult = yield call(performMediaVerification, mediaVerifyPayload);

      if (!verificationResult?.success) {
        const errorMsg = verificationResult?.error || 'Failed to verify media';
        yield put(setError(errorMsg));
        yield put(showAlert({ message: errorMsg, type: 'error' }));
        return;
      }

      headerHandle = verificationResult.headerHandle;
      yield put(showAlert({ message: 'Media verified successfully', type: 'success' }));
    }

    const reqBody = buildTemplateRequestUtil({
      wabaId,
      name: payload.templateName,
      category: categoryNormalized,
      language: payload.language,
      parameter_format: payload.parameter_format,
      headerType: payload.headerType,
      headerText: payload.headerText,
      bodyText: payload.templateBody,
      paramExamples: payload.variableSamples,
      buttons: payload.buttons,
      headerHandle,
      mediaType,
      isAuthTemplate: payload.isAuthTemplate || false,
      messageSendTtlSeconds: payload.messageSendTtlSeconds || 600,
      addSecurityRecommendation: payload.addSecurityRecommendation || false,
      codeExpirationMinutes: payload.codeExpirationMinutes || 10,
      otpType: payload.otpType || 'copy_code',
      otpButtonText: payload.otpButtonText || getAuthOtpButtonText(payload.language),
      libraryTemplateName: payload.libraryTemplateName,
      libraryTemplateButtonInputs: payload.libraryTemplateButtonInputs
    });

    const res = yield call(callWithAuth, createTemplateApi, reqBody);

    if (res?.status === true) {
      const returnedStatus = res?.data?.status || 'PENDING';
      const returnedCategory = res?.data?.category || categoryNormalized;

      yield put(addTemplate({
        name: payload.templateName,
        description: '',
        body: payload.templateBody,
        category: returnedCategory,
        status: returnedStatus
      }));

      yield put(setSuccess(true));
      yield put(showAlert({ message: `Template submitted for approval (${returnedStatus})`, type: 'success' }));
      yield delay(600);
      yield put(resetWhatsappState());
      yield put(clearMediaVerification());
    } else {
      const errorMsg = res?.errMsg || 'Failed to create template';
      yield put(setError(errorMsg));
      yield put(showAlert({ message: errorMsg, type: 'error' }));
    }
  } catch (error) {
    console.log('Error in handleCreateTemplate saga:', error);
    yield put(setError('Error occurred while creating template'));
    yield put(showAlert({ message: 'Error occurred while creating template', type: 'error' }));
  } finally {
    yield put(setLoading(false));
  }
}
